<script setup lang="ts">
import type { ButtonProps } from '#ui/types';
import type { PropType } from 'vue';

// COMPOSABLES
const { isMobile } = useResponsive();
const { swiperElement, activeIndex } = useSwiperNavigation();

// PROPS
defineProps({
    items: { type: Array as PropType<string[]>, required: true },
    initialSlide: { type: Number, default: 0 },
    title: { type: String, default: undefined },
});

// EMITS
const emit = defineEmits<{
    activeIndexChange: [activeIndex: number];
}>();

// DATA
const model = defineModel<boolean>();

const buttonSize = computed<ButtonProps['size']>(() => (isMobile.value ? 'lg' : 'xl'));

// WATCHERS
watch(activeIndex, value => emit('activeIndexChange', value));
</script>

<template>
    <SdpLightboxWrapper v-model="model" index-i18n-key="sdp.imagePagination" :index="activeIndex" :total-items="items.length" :title="title">
        <SdpSwiperNavigation
            class="w-[calc(100%-2.25rem)] max-w-7xl lg:w-[calc(100%-3.25rem)] portrait:top-[calc(50dvh-2.5rem)] landscape:top-[calc(50dvh-1.5rem)] landscape:lg:top-[calc(50dvh-3rem)]"
            :swiper-element="swiperElement"
            :size="buttonSize"
            :active-index="activeIndex"
            rewind
        />

        <swiper-container
            ref="swiperElement"
            class="max-w-full rounded-xl"
            :initial-slide="initialSlide"
            :space-between="24"
            :breakpoints="{
                768: {
                    spaceBetween: 32,
                },
            }"
            :lazy-preload-prev-next="1"
            thumbs-swiper=".sdp-lightbox-img-thumbs"
            :zoom="{
                toggle: true,
            }"
            auto-height="true"
            touch-start-prevent-default="false"
            rewind="true"
            virtual="true"
        >
            <swiper-slide
                v-for="(image, index) in items"
                :key="image"
                lazy="true"
                class="[&.swiper-slide-zoomed_.swiper-zoom-container_img]:cursor-zoom-out [&.swiper-slide-zoomed_.swiper-zoom-container_img:active]:cursor-move"
            >
                <div class="swiper-zoom-container [&_img]:cursor-zoom-in">
                    <NuxtImg
                        class="mx-auto aspect-4/3 rounded-xl border object-cover landscape:max-h-[calc(100dvh-4rem)] landscape:lg:max-h-[calc(100dvh-14rem)]"
                        :src="image"
                        :preload="index === 0"
                        sizes="1280 lg:2560"
                        densities="x1"
                        loading="lazy"
                        :draggable="false"
                    />
                </div>
            </swiper-slide>
        </swiper-container>

        <swiper-container
            class="sdp-lightbox-img-thumbs [&_.swiper-slide-thumb-active_img]:border-primary-950 max-w-full landscape:hidden lg:landscape:block"
            :space-between="24"
            :slides-per-view="Math.min(3, items.length)"
            :breakpoints="{
                640: {
                    slidesPerView: Math.min(4, items.length),
                },
                768: {
                    slidesPerView: Math.min(5, items.length),
                    spaceBetween: 28,
                },
                1024: {
                    slidesPerView: Math.min(7, items.length),
                    spaceBetween: 32,
                },
                1280: {
                    slidesPerView: Math.min(8, items.length),
                    spaceBetween: 32,
                },
            }"
            rewind="true"
            virtual="true"
        >
            <swiper-slide v-for="image in items" :key="image" lazy="true'">
                <NuxtImg
                    class="aspect-4/3 cursor-pointer rounded-xl border-2 object-cover"
                    :src="image"
                    :width="240"
                    densities="x1"
                    loading="lazy"
                    :draggable="false"
                />
            </swiper-slide>
        </swiper-container>
    </SdpLightboxWrapper>
</template>
